import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from '../../common/Toast/toast';
import PopupDone, { usePopupDone } from "../../common/PopupDone/PopupDone.jsx";
import { deleteUser, getUser, getUsers } from "../../../../services/UserSerivce.js";
import { getappstoroles } from "../../../../services/AppToRole.js";
import { useAuth } from "../../../../context/AuthContext";
import { Users } from "lucide-react";
import NewCustomDataTable from "../../common/NewCustomDataTable.jsx";
import SettingsFormModal from "../../common/SettingsFormModal.jsx";
import AddStudentUser from "./AddStudentUser.jsx";
import useScrollLoadMore from "../../common/useScrollLoadMore";
import { initialsOf, avatarTone } from "../../Trainer/Attendance/AttendanceBits.jsx";
// Built to the same plan as /list-attendance, so it reads that page's
// single stylesheet rather than keeping one of its own.
import "../../Trainer/TrainerDashboard.css";
import FilterSheet from "../../common/FilterSheet/FilterSheet";
import useDebouncedCallback from "../../common/useDebouncedCallback";
import { confirm } from '../../common/Confirm/confirm';

// How many users one server call brings back. Not the same thing as a
// scroll batch: the endpoint returns every kind of user and only some of
// them are students, so several calls may be needed to fill one batch.
const SERVER_FETCH_LIMIT = 50;

const DASH = "—";

const isMongoObjectId = (id) => /^[a-f\d]{24}$/i.test(id);

const capitalizeWords = (str) => {
  if (!str) return "";
  return str
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
};

/* The status pill, in one place: the Status column shows it on a desktop
   and the Student cell carries it on a phone, where that column is gone.
   Returns null for a blank status so each caller decides what a missing
   one looks like. */
const statusPill = (raw, extraClass = "") => {
  const status = String(raw || "").toLowerCase();
  if (!status) return null;
  return (
    <span
      className={`pg-pill ${
        status === "active" ? "pg-pill--good" : "pg-pill--bad"
      }${extraClass ? ` ${extraClass}` : ""}`}
    >
      {capitalizeWords(status)}
    </span>
  );
};

const dateOf = (value) => {
  if (!value) return DASH;
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? DASH
    : d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
};

/* ── The record popup ────────────────────────────────────────────────
   Same plan as the attendance list's popup: title and a pgm-ident row of
   labelled facts in the header, pgm-section blocks in the body each led
   by a pgm-section-title, pgm-table for anything with more than one
   value, pgm-empty when there is nothing, and a footer whose actions sit
   left of a filled Close.
   ------------------------------------------------------------------ */
const StudentUserDetailModal = ({ detail, onClose, onEdit, onDelete }) => {
  useEffect(() => {
    if (!detail) return undefined;
    const onKey = (e) => {
      // A form opened from here sits on top; Escape is its, not this popup's.
      if (e.key === "Escape" && !document.querySelector(".pg-modal--over")) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [detail, onClose]);

  // A delete from here swaps the record for the done panel, which closes
  // the popup five seconds later.
  const { done } = usePopupDone(Boolean(detail));

  if (!detail) return null;

  const {
    id,
    name,
    email,
    mobileNumber,
    statusLabel,
    isActive,
    createdAt,
    updatedAt,
    firstName,
    lastName,
    applications,
    roleGroups,
    canDelete,
  } = detail;

  const account = [
    { label: "First name", value: firstName || DASH },
    { label: "Last name", value: lastName || DASH },
    { label: "Email", value: email || DASH },
    { label: "Contact number", value: mobileNumber || DASH },
    { label: "Created", value: dateOf(createdAt) },
    { label: "Last updated", value: dateOf(updatedAt) },
  ];

  return (
    <div
      className="pg-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="suser-detail-title"
      onClick={onClose}
    >
      <div
        className="pg-modal-panel pg-modal-panel--wide"
        onClick={(e) => e.stopPropagation()}
      >
        {/* The person is what the record is about, so they are the title. */}
        <header className="pgm-head">
          <div className="pgm-head-main">
            <h2 className="pgm-batch" id="suser-detail-title">
              {name}
            </h2>
            <dl className="pgm-ident">
              <div className="pgm-ident-row">
                <dt>Email</dt>
                <dd>{email || DASH}</dd>
              </div>
              <div className="pgm-ident-row">
                <dt>Contact</dt>
                <dd>{mobileNumber || DASH}</dd>
              </div>
              <div className="pgm-ident-row">
                <dt>Status</dt>
                <dd>{statusLabel}</dd>
              </div>
            </dl>
          </div>

          <button
            type="button"
            className="pg-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
        </header>

        <div className="pg-modal-body">
          {done ? (
            <PopupDone message={done} onClose={onClose} />
          ) : (
            <>
              <section className="pgm-section">
                <h3 className="pgm-section-title">Account</h3>
                {/* --values: the shared .pgm-table hides its third column under
                    576px, which in these three tables is the value itself --
                    on a phone the Account table listed six labels and nothing
                    else. The scroller is what lets a long email be read at
                    that width rather than wrapping to four lines. */}
                <div className="pgm-tablescroll">
                <table className="pgm-table pgm-table--values">
                  <thead>
                    <tr>
                      <th className="pgm-num">#</th>
                      <th>Detail</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {account.map((f, i) => (
                      <tr key={f.label}>
                        <td className="pgm-num">{i + 1}</td>
                        <td>{f.label}</td>
                        <td>{f.value}</td>
                      </tr>
                    ))}
                    <tr>
                      <td className="pgm-num">{account.length + 1}</td>
                      <td>Status</td>
                      <td>
                        <span
                          className={`pgm-pill ${
                            isActive ? "pgm-pill--good" : "pgm-pill--bad"
                          }`}
                        >
                          {statusLabel}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
                </div>
              </section>

              <section className="pgm-section">
                <h3 className="pgm-section-title">
                  Applications
                  <span className="pgm-section-count">{applications.length}</span>
                </h3>

                {applications.length === 0 ? (
                  <p className="pgm-empty">
                    This account is not assigned to any application.
                  </p>
                ) : (
                  <div className="pgm-tablescroll">
                    <table className="pgm-table">
                      <thead>
                        <tr>
                          <th className="pgm-num">#</th>
                          <th>Application</th>
                        </tr>
                      </thead>
                      <tbody>
                        {applications.map((app, i) => (
                          <tr key={app}>
                            <td className="pgm-num">{i + 1}</td>
                            <td>{app}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              {/* This used to be an HTML string built with <strong> tags and
                  handed to dangerouslySetInnerHTML inside a table cell. It is
                  a table now, which is what it always was. */}
              <section className="pgm-section">
                <h3 className="pgm-section-title">
                  Roles by application
                  <span className="pgm-section-count">{roleGroups.length}</span>
                </h3>

                {roleGroups.length === 0 ? (
                  <p className="pgm-empty">No roles are assigned to this account.</p>
                ) : (
                  <div className="pgm-tablescroll">
                    <table className="pgm-table pgm-table--values">
                      <thead>
                        <tr>
                          <th className="pgm-num">#</th>
                          <th>Application</th>
                          <th>Roles</th>
                        </tr>
                      </thead>
                      <tbody>
                        {roleGroups.map((group, i) => (
                          <tr key={group.app}>
                            <td className="pgm-num">{i + 1}</td>
                            <td>{group.app}</td>
                            <td>{group.roles.join(", ")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </>
          )}
        </div>

        {/* What you can do with the record sits with Close, at the end.
            Close stays last and is the only filled button, so the
            destructive one is never the default under the cursor. */}
        {/* The done panel replaces the actions once a delete lands. */}
        {!done && (
          <footer className="pg-modal-foot">
            <div className="pgm-actions">
              <button type="button" className="pgm-btn" onClick={onEdit}>
                Edit
              </button>
              {canDelete && (
                <button
                  type="button"
                  className="pgm-btn pgm-btn--danger"
                  onClick={onDelete}
                >
                  Delete
                </button>
              )}
            </div>
            <button type="button" className="pg-btn pg-btn--main" onClick={onClose}>
              Close
            </button>
          </footer>
        )}
      </div>
    </div>
  );
};

const ListStudentUsers = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  // Fetching the next scroll batch. Kept apart from `loading` because that
  // one draws a veil over the whole table -- right for a fresh load, wrong
  // for adding rows to the bottom of a list being read.
  const [loadingMore, setLoadingMore] = useState(false);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [exhausted, setExhausted] = useState(false);
  const [detail, setDetail] = useState(null);
  /* The add/edit dialog. null is closed; {} is a new student;
     { recordId } edits that one. */
  const [formModal, setFormModal] = useState(null);
  /* Which step the form is on, and its question -- the dialog head asks
     it. Cleared with the dialog, so the next open starts on step one's
     copy rather than flashing the step the last one ended on. */
  const [formStep, setFormStep] = useState(null);

  // Closing the dialog and forgetting the step it was on are the same act,
  // so nothing can do one without the other.
  const closeForm = useCallback(() => {
    setFormModal(null);
    setFormStep(null);
  }, []);

  // What is typed into the filter section but not yet applied.
  const [searchDraft, setSearchDraft] = useState("");
  const [searchFieldDraft, setSearchFieldDraft] = useState("email");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchField, setSearchField] = useState("email");

  // How many student users a scroll batch adds, not how many the reader
  // has to click through -- the list scrolls rather than paging.
  const [batchSize, setBatchSize] = useState(25);

  // Roles
  const rolesMapRef = useRef({});
  const studentRoleRef = useRef(null);
  /* Set once the roles have been read. Nothing may fill the list before
     then: a fill run without the Student role keeps no one but still
     moves the server page on, so when the roles landed the list started
     from wherever that page had got to -- a different, older stretch of
     students on every refresh, and never the newest. */
  const rolesReadyRef = useRef(false);

  // Every student user fetched so far, and how far into the server's own
  // pages that took.
  const bufferRef = useRef([]);
  const serverPageRef = useRef(0);
  const serverHasMoreRef = useRef(true);
  const isFetching = useRef(false);
  /* Which run of the list the results in flight belong to. Applying a
     filter clears the buffer and resets the server page under a request
     that has already been sent, so without this its answer would be
     appended to the new list -- old rows, under a new search. */
  const genRef = useRef(0);
  // A filter applied while a fetch is still in flight. Without this the
  // guard below simply dropped it and Apply looked like a dead button.
  const pendingRef = useRef(null);
  const listRef = useRef(null);
  // The foot under the list. It is what the observer watches, and it
  // sits in the normal flow of the page -- no pane of its own.
  const listEndRef = useRef(null);
  const fillRef = useRef(null);

  const navigate = useNavigate();
  const hasShownToast = useRef(false);
  const { hasPermission, user: currentUser } = useAuth();

  useEffect(() => {
    if (!hasPermission("student-users", "view", "lms")) {
      if (!hasShownToast.current) {
        navigate("/");
        hasShownToast.current = true;
      }
    }
  }, [hasPermission, navigate]);

  // ─── Helpers ──────────────────────────────────────────────────────────

  /* Roles grouped under the application they belong to. Returned as data
     rather than as a string of markup, so the popup can lay it out as a
     table and the list can count it. */
  const roleGroupsOf = (profileIds, map = rolesMapRef.current) => {
    if (!Array.isArray(profileIds) || profileIds.length === 0) return [];

    const byApp = {};
    profileIds.forEach((roleId) => {
      const role = map[roleId];
      if (role) {
        const app = role.application || "Other";
        if (!byApp[app]) byApp[app] = [];
        byApp[app].push(role.name);
      } else if (!isMongoObjectId(roleId)) {
        if (!byApp.Other) byApp.Other = [];
        byApp.Other.push(capitalizeWords(roleId));
      }
    });

    return Object.entries(byApp).map(([app, roles]) => ({
      app: capitalizeWords(app),
      roles,
    }));
  };

  const applicationsOf = (user, map = rolesMapRef.current) => {
    if (typeof user.applications === "string") {
      return user.applications
        .split(",")
        .map((a) => a.trim())
        .filter(Boolean);
    }
    if (Array.isArray(user.applications) && user.applications.length > 0) {
      return user.applications.map(capitalizeWords);
    }
    if (Array.isArray(user.profileIds) && user.profileIds.length > 0) {
      const apps = new Set();
      user.profileIds.forEach((roleId) => {
        const role = map[roleId];
        if (role?.application) apps.add(role.application);
      });
      return Array.from(apps).map(capitalizeWords);
    }
    return [];
  };

  // Check if a user is a student
  const isStudentUser = (user) => {
    const sRole = studentRoleRef.current;
    if (!sRole) return false;
    const profileIds = user.profileIds || [];
    return (
      profileIds.includes(sRole.id) ||
      (sRole.value && profileIds.includes(sRole.value))
    );
  };

  // Transform raw user from API into display-ready object
  const transformUser = (user) => {
    const profileIds = user.profileIds
      ? Array.isArray(user.profileIds)
        ? user.profileIds
        : user.profileIds.split(",")
      : [];
    const apps = applicationsOf({ ...user, profileIds });
    return {
      ...user,
      profileIds,
      roles: user.roles || profileIds,
      applicationList: apps,
      applications: apps.join(", "),
    };
  };

  // ─── Fetch roles ──────────────────────────────────────────────────────

  const fetchRoles = useCallback(async () => {
    try {
      setRolesLoading(true);
      const response = await getappstoroles();
      const rolesMapData = {};
      let foundStudentRole = null;

      if (response.data && Array.isArray(response.data)) {
        foundStudentRole = response.data.find(
          (role) => role.label === "Student" && role.value === "student",
        );

        response.data.forEach((role) => {
          const applicationName = role.appstorole?.[0]?.app?.label || "Other";

          rolesMapData[role.id] = {
            name: role.label || role.name || `Role ${role.id}`,
            application: applicationName,
          };

          if (role.value && role.value !== role.id) {
            rolesMapData[role.value] = {
              name: role.label || role.name || `Role ${role.value}`,
              application: applicationName,
            };
          }

          if (role.profileIds) {
            const profileIdsArray = Array.isArray(role.profileIds)
              ? role.profileIds
              : role.profileIds.split(",");
            profileIdsArray.forEach((profileId) => {
              if (profileId.trim()) {
                rolesMapData[profileId.trim()] = {
                  name: role.label || role.name || `Role ${profileId}`,
                  application: applicationName,
                };
              }
            });
          }
        });
      }

      rolesMapRef.current = rolesMapData;
      studentRoleRef.current = foundStudentRole;
      return rolesMapData;
    } catch (error) {
      console.error("Error fetching roles:", error);
      return {};
    } finally {
      rolesReadyRef.current = true;
      setRolesLoading(false);
    }
  }, []);

  // ─── Filling the list ─────────────────────────────────────────────────

  /* Keeps pulling server pages until the buffer holds `target` student
     users or the server runs out. The endpoint returns users of every
     kind and pages over all of them, so one scroll batch of students can
     take several calls -- which is why the count in the footer is what
     has been found, not a total the server never reported. */
  const fillTo = async (target, append, term, field) => {
    // Not before the roles are known -- see rolesReadyRef. A search typed
    // in that moment waits its turn; a scroll batch is simply dropped,
    // since the first fill after the roles is about to run anyway.
    if (!rolesReadyRef.current) {
      if (!append) pendingRef.current = { target, append, term, field };
      return;
    }
    if (isFetching.current) {
      /* A scroll batch asked for while one is already running is simply
         dropped -- the running one is fetching those rows anyway. A
         filter is not: it replaces the list, so it waits its turn. */
      if (!append) pendingRef.current = { target, append, term, field };
      return;
    }
    isFetching.current = true;
    const mine = genRef.current;
    if (append) setLoadingMore(true);
    else setLoading(true);

    try {
      while (bufferRef.current.length < target && serverHasMoreRef.current) {
        const nextServerPage = serverPageRef.current + 1;
        const searchIn = field && field !== "all" ? field : "";
        const response = await getUsers(
          SERVER_FETCH_LIMIT,
          nextServerPage,
          term || "",
          searchIn,
        );

        // A filter was applied while this page was in the air. Its
        // rows belong to a list that no longer exists.
        if (mine !== genRef.current) return;

        const usersData = response?.data || [];
        const pagination = response?.pagination || {};
        serverPageRef.current = nextServerPage;

        const serverTotal = Number(pagination.total || 0);
        if (
          usersData.length < SERVER_FETCH_LIMIT ||
          (serverTotal > 0 && nextServerPage * SERVER_FETCH_LIMIT >= serverTotal)
        ) {
          serverHasMoreRef.current = false;
        }

        // Guarded by id so a repeated page cannot double up a row.
        const seen = new Set(bufferRef.current.map((u) => u.id));
        const studentUsers = usersData
          .map(transformUser)
          .filter(isStudentUser)
          .filter((u) => !seen.has(u.id));

        bufferRef.current = [...bufferRef.current, ...studentUsers];

        if (usersData.length === 0) {
          serverHasMoreRef.current = false;
          break;
        }
      }

      setRows(bufferRef.current.slice());
      setExhausted(!serverHasMoreRef.current);
    } catch (error) {
      console.error("Error fetching users:", error);
      if (mine !== genRef.current) return;
      serverHasMoreRef.current = false;
      setExhausted(true);
      toast.error("Failed to fetch student users");
    } finally {
      setLoading(false);
      setLoadingMore(false);
      isFetching.current = false;

      const queued = pendingRef.current;
      if (queued) {
        pendingRef.current = null;
        fillRef.current(queued.target, queued.append, queued.term, queued.field);
      }
    }
  };

  fillRef.current = fillTo;

  const resetBuffer = () => {
    genRef.current += 1;
    bufferRef.current = [];
    serverPageRef.current = 0;
    serverHasMoreRef.current = true;
    setExhausted(false);
    setRows([]);
  };

  /* An edit changes one row, so only that row is re-read and put back in
     its own place. Emptying the buffer and refilling it -- what a new
     student still gets -- collapsed the table to nothing, threw the reader
     back to the top, refilled only the first batch, and let the server's
     order move the edited row somewhere else. Falls back to the full
     refill only if the record cannot be read. */
  const refreshRow = async (id) => {
    try {
      const res = await getUser(id);
      const fresh = res?.data;
      if (!fresh) throw new Error("No record returned");
      const updated = transformUser(fresh);
      bufferRef.current = bufferRef.current.map((u) =>
        String(u.id) === String(id) ? { ...u, ...updated } : u,
      );
      setRows(bufferRef.current.slice());
    } catch (error) {
      console.error("Error refreshing student user:", error);
      resetBuffer();
      fillRef.current(batchSize, false, searchTerm, searchField);
    }
  };

  // ─── Init ─────────────────────────────────────────────────────────────

  useEffect(() => {
    fetchRoles().then(() => {
      fillRef.current(batchSize, false, "", "email");
    });
    // Runs once: the roles map has to exist before a user can be told to
    // be a student, and every later fill is driven by a handler.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Scroll ───────────────────────────────────────────────────────────

  // The scroll asked for the next batch. Only ever moves forward, and only
  // while the server has more than is already on screen.
  const loadMore = useCallback(() => {
    // The empty list's foot is on screen at once; it must not page ahead
    // of the first fill.
    if (!rolesReadyRef.current || bufferRef.current.length === 0) return;
    if (isFetching.current) return;
    if (!serverHasMoreRef.current) return;
    fillRef.current(bufferRef.current.length + batchSize, true, searchTerm, searchField);
  }, [batchSize, searchTerm, searchField]);

  // The page is the scroller, so the foot of the list coming into view is
  // the signal for the next batch -- see useScrollLoadMore.
  useScrollLoadMore(listEndRef, loadMore, [loadMore, rows.length]);

  // ─── Handlers ─────────────────────────────────────────────────────────

  /* The search box does not wait for Apply. It runs itself once the
     typing stops -- the pickers beside it still wait, because choosing
     three of those should cost one refetch, not three.

     This page fills a buffer rather than turning a page, so it asks for
     the refill the same way Apply does. */
  const runSearch = useDebouncedCallback((value, field) => {
    const term = value.trim();
    setSearchTerm(term);
    setSearchField(field);
    resetBuffer();
    fillRef.current(batchSize, false, term, field);
  });

  const handleApplyFilters = () => {
    /* This sets the search itself, so a keystroke still waiting out its
       delay must not land on top of it a moment later. */
    runSearch.cancel();
    const term = searchDraft.trim();
    setSearchTerm(term);
    setSearchField(searchFieldDraft);
    resetBuffer();
    fillRef.current(batchSize, false, term, searchFieldDraft);
  };

  const handleClearFilters = () => {
    runSearch.cancel();
    setSearchDraft("");
    setSearchFieldDraft("email");
    setSearchTerm("");
    setSearchField("email");
    resetBuffer();
    fillRef.current(batchSize, false, "", "email");
  };

  const handleBatchSizeChange = (value) => {
    setBatchSize(Number(value));
  };

  const hasActiveFilters = Boolean(searchTerm) || searchField !== "email";

  const handleDelete = async (id) => {
    if (currentUser && currentUser.id === id) {
      toast.warning("You cannot delete your own account.");
      return;
    }

    const result = await confirm({
      title: "Delete Confirmation",
      tone: 'danger',
      text: "Are you sure you want to delete this user?",
      confirmButtonText: "Yes, Delete it!",
      cancelButtonText: "No, Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      await deleteUser(id);
      // Gone: the popup that showed it swaps to the done panel and closes
      // itself five seconds later.
      bufferRef.current = bufferRef.current.filter((u) => u.id !== id);
      setRows(bufferRef.current.slice());
      toast.success("The user has been deleted successfully.");
    } catch (error) {
      console.error("Delete user error:", error);
      toast.error("Failed to delete the user. Please try again.");
    }
  };

  // ─── Opening a record ─────────────────────────────────────────────────

  const openRecord = (row) => {
    if (!row) return;
    const status = String(row.status || "").toLowerCase();
    const name = `${row.firstName || ""} ${row.lastName || ""}`.trim();

    setDetail({
      id: row.id,
      name: name || row.email || "Student user",
      firstName: row.firstName,
      lastName: row.lastName,
      email: row.email,
      mobileNumber: row.mobileNumber,
      statusLabel: status ? capitalizeWords(status) : DASH,
      isActive: status === "active",
      createdAt: row.createdAt,
      updatedAt: row.updatedAt,
      applications: row.applicationList || applicationsOf(row),
      roleGroups: roleGroupsOf(row.profileIds),
      canDelete: Boolean(currentUser && currentUser.id !== row.id),
    });
  };

  // ─── Headers ──────────────────────────────────────────────────────────

  /* Five columns and no more. Sr No, the applications, the roles, the
     update stamp and the row of action buttons are all in the popup a row
     opens -- the same trade the attendance list makes. */
  const headers = [
    {
      title: "Student",
      prop: "firstName",
      searchable: true,
      filterable: true,
      sortable: true,
      cell: (row) => {
        const name = `${row.firstName || ""} ${row.lastName || ""}`.trim();
        return (
          <div className="td-person">
            <span
              className={`td-person-avatar td-person-avatar--${avatarTone(name)}`}
              aria-hidden="true"
            >
              {initialsOf(name)}
            </span>
            <span className="td-person-id">
              <b className="td-person-name">{name || DASH}</b>
              {/* Under 768px the list collapses to its first two columns
                  and drops the header row, which takes Status with it --
                  and an inactive account then looks exactly like a live
                  one. It rides under the name at that width instead.
                  Hidden above it, where the Status column says it. */}
              {statusPill(row.status, "suser-rowstatus")}
            </span>
          </div>
        );
      },
    },
    {
      title: "Email",
      prop: "email",
      searchable: true,
      filterable: true,
      sortable: true,
      cell: (row) =>
        row.email ? <span>{row.email}</span> : <span className="pg-none">{DASH}</span>,
    },
    {
      title: "Contact Number",
      prop: "mobileNumber",
      searchable: true,
      filterable: true,
      sortable: true,
      cell: (row) =>
        row.mobileNumber ? (
          <span className="td-person-tel">{row.mobileNumber}</span>
        ) : (
          <span className="pg-none">{DASH}</span>
        ),
    },
    {
      title: "Status",
      prop: "status",
      filterable: true,
      sortable: true,
      cell: (row) => statusPill(row.status) || <span className="pg-none">{DASH}</span>,
    },
    {
      title: "Created",
      prop: "createdAt",
      filterable: true,
      sortable: true,
      cell: (row) => <span className="pg-date">{dateOf(row.createdAt)}</span>,
    },
  ];

  // ─── Render ───────────────────────────────────────────────────────────

  return (
    <div className="pg">

      <SettingsFormModal
        open={Boolean(formModal)}
        /* The form reports its question and the dialog asks it, rather
           than repeating a fixed title over the content. It is one step
           now -- the roles step went, since every account here is a
           student -- so the report arrives once and does not change
           after. The fallbacks are what shows for the frame before it. */
        eyebrow={formModal?.recordId ? "Edit student" : "New student"}
        title={formStep?.title || "Who is this student?"}
        subtitle={
          formStep?.hint || "Their name, how to reach them, and how they sign in."
        }
        labelledBy="student-form-title"
        size="wide"
        onClose={closeForm}
      >
        <AddStudentUser
          embedded
          recordId={formModal?.recordId}
          onStepChange={(copy) => setFormStep(copy)}
          onCancel={closeForm}
          onDone={() => {
            // Saved: the dialog swaps the form for the done panel and closes
            // itself five seconds later (SettingsFormModal).
            // The record changed, so the view popup still open behind this form
            // would be showing the old one. It goes; the form shows the done panel.
            setDetail(null);
            const editedId = formModal?.recordId;
            if (editedId) {
              refreshRow(editedId);
              return;
            }
            // A new student: this list fills from a buffer rather than
            // paging, so a refresh is "empty it and fill again" -- under
            // whatever search is currently applied, not from scratch.
            resetBuffer();
            fillRef.current(batchSize, false, searchTerm, searchField);
          }}
        />
      </SettingsFormModal>

      <StudentUserDetailModal
        detail={detail}
        onClose={() => setDetail(null)}
        onEdit={() => {
          const id = detail?.id;
          if (id) setFormModal({ recordId: String(id) });
        }}
        onDelete={() => {
          const id = detail?.id;
          if (id) handleDelete(id);
        }}
      />

      {/* Every control that narrows the list lives here, the search box
          included -- the table's own toolbar is switched off below so the
          two are not stacked one above the other. Nothing here takes
          effect until Apply, so changing two fields is one refetch. */}
      <FilterSheet label="Filter student users">
        {/* Row one: the search box, the field it searches, and how many
            rows a load brings. */}
        <div className="pg-filters-search">
          <div className="pg-field pg-field--search">
            <label className="pg-label" htmlFor="suser-search">
              Search
            </label>
            <input
              id="suser-search"
              type="text"
              className="pg-input pg-input--search"
              placeholder="Search by name, email or contact number..."
              value={searchDraft}
              onChange={(e) => {
                setSearchDraft(e.target.value);
                runSearch(e.target.value, searchFieldDraft);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleApplyFilters();
              }}
            />
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="suser-search-field">
              Search in
            </label>
            <select
              id="suser-search-field"
              className="pg-select"
              value={searchFieldDraft}
              onChange={(e) => {
                setSearchFieldDraft(e.target.value);
                if (searchDraft.trim()) runSearch.flush(searchDraft, e.target.value);
              }}
            >
              <option value="all">All fields</option>
              <option value="email">Email</option>
              <option value="firstName">First name</option>
              <option value="lastName">Last name</option>
              <option value="mobileNumber">Contact number</option>
            </select>
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="suser-rows">
              Rows per load
            </label>
            <select
              id="suser-rows"
              className="pg-select"
              value={batchSize}
              onChange={(e) => handleBatchSizeChange(e.target.value)}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        {/* Row two: the buttons. */}
        <div className="pg-filters-row">
          <div className="pg-filters-fields pg-filters-fields--notrainer" />

          <div className="pg-filters-actions">
            <button
              type="button"
              className="pg-btn pg-btn--main"
              onClick={handleApplyFilters}
            >
              Apply
            </button>
            <button
              type="button"
              className="pg-btn"
              onClick={handleClearFilters}
              disabled={!hasActiveFilters}
            >
              Clear
            </button>

            <span className="pg-filters-sep" aria-hidden="true" />

<button
              type="button"
              className="pg-btn pg-btn--main"
              onClick={() => setFormModal({})}
            >
              Add student user
            </button>
          </div>
        </div>
      </FilterSheet>

      {/* --suser: the prefix has to be its own, the rules that give a row
          its pointer and hover are unscoped and would otherwise reach into
          every other list sharing a modifier. */}
      <div className="td-list td-list--suser" ref={listRef}>
        <NewCustomDataTable
          data={rows}
          headers={headers}
          loading={loading}
          loadingMore={loadingMore}
          showToolbar={false}
          showPagination={false}
          showFooter={false}
          onRowClick={(row) => openRecord(row)}
          // Every cell wraps its text in a span, so the default
          // ['TR', 'TD'] would leave the text itself unclickable and only
          // the padding around it would open the record.
          validRowClickTagNames="*"
          rowProps={(row) => ({
            tabIndex: 0,
            role: "button",
            "aria-label": `Open ${
              `${row.firstName || ""} ${row.lastName || ""}`.trim() ||
              row.email ||
              "this student user"
            }`,
            onKeyDown: (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                openRecord(row);
              }
            },
          })}
        />
      </div>

      {/* What the page buttons used to say, said once: how far down the
          list the reader is. Scrolling near the bottom fetches the next
          batch.

          It counts what has been found rather than reporting a total: the
          users endpoint pages over every kind of user and only reports how
          many of those there are, so a "of N" here would be a number that
          has nothing to do with student users. */}
      <div className="pg-scroll-foot" ref={listEndRef}>
        {!exhausted ? (
          <>
            <span className="pg-scroll-count">{rows.length} loaded</span>
            {loadingMore && <span className="pg-spinner" aria-hidden="true" />}
          </>
        ) : (
          rows.length > 0 && (
            <span className="pg-scroll-count">
              All {rows.length} student user{rows.length === 1 ? "" : "s"} loaded
            </span>
          )
        )}
      </div>

      {!loading && !rolesLoading && rows.length === 0 && (
        <div className="pg-state">
          <span className="pg-state-icon">
            <Users size={26} aria-hidden="true" />
          </span>
          <h3 className="pg-state-title">No student users found</h3>
          <p className="pg-state-text">
            {searchTerm
              ? `Nothing matches “${searchTerm}”.`
              : "No user account carries the student role."}
          </p>
        </div>
      )}
    </div>
  );
};

export default ListStudentUsers;
